<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1>Dispense Logs</h1>
        <DateRangePicker
          v-bind:dateMin.sync="dateMin"
          v-bind:dateMax.sync="dateMax"
          v-bind:timeMin.sync="timeMin"
          v-bind:timeMax.sync="timeMax"
          show-time
        />
        <v-row>
          <v-col cols="6" sm="3">
            <v-autocomplete
              v-model="filterPlantUsers"
              :items="plantUsers"
              label="Users"
              item-text="name"
              item-value="id"
              prepend-icon="mdi-account"
              dense
              chips
              small-chips
              deletable-chips
              multiple
            ></v-autocomplete>
          </v-col>
          <v-col cols="6" sm="3">
            <v-autocomplete
              v-model="filterApps"
              :items="apps"
              label="Application"
              item-text="name"
              item-value="id"
              prepend-icon="mdi-application-import"
              dense
              chips
              small-chips
              deletable-chips
              multiple
            ></v-autocomplete>
          </v-col>
          <v-col cols="6" sm="3">
            <v-autocomplete
              v-model="filterChemicals"
              :items="chemicals"
              label="Chemical"
              item-text="name"
              item-value="id"
              prepend-icon="mdi-bottle-tonic"
              dense
              chips
              small-chips
              deletable-chips
              multiple
            ></v-autocomplete>
          </v-col>
          <v-col cols="6" sm="3">
            <v-autocomplete
              v-model="filterLocations"
              :items="locations"
              label="Location"
              prepend-icon="mdi-office-building"
              dense
              chips
              small-chips
              deletable-chips
              multiple
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-container>
            <v-row align="center" justify="end">
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" class="mr-3" color="secondary">
                    <v-icon>mdi-download</v-icon> &nbsp; Download
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(item, index) in downloadOptions"
                    :key="index"
                    @click="item.callback"
                  >
                    <v-list-item-title>{{ item.title }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-row>
          </v-container>
        </v-row>
        <v-row v-if="overApiLimitWarning">
          <v-col>
            <v-alert type="warning" dense color="orange" border="left">
              {{ overApiLimitWarning }}
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <Loading v-if="!fullyLoaded" />
            <DataTable
              v-else
              icon="mdi-file-chart"
              :items="items"
              :headers="headers"
              :sortBy="sortBy"
              :loading="!fullyLoaded"
              :sortDesc="true"
              :showExpand="true"
            >
              <template v-slot:expando="{ item }">
                <td :colspan="headers.length">
                  <v-container>
                    <v-row>
                      <v-col cols="6">
                        <h4>Dispense Log Details</h4>
                        <p>
                          Logged in at {{ item.loginTime | longDateFormat(timeZone) }}<br />
                          Started Dispense at {{ item.startTime | longDateFormat(timeZone) }}<br />
                          Finished Dispense at {{ item.stopTime | longDateFormat(timeZone) }}<br />
                        </p>
                      </v-col>
                      <v-col cols="6">
                        <h4>Chemical Breakdown</h4>
                        <ol v-if="isMetric">
                          <li v-for="(step, i) in item.dispenseSteps" :key="i">
                            {{ (step.volume * metricVolumeConversion) | rounded }} L /
                            {{ (step.weight * metricWeightConversion) | rounded }} kg of
                            {{ step.chemical.name }}
                          </li>
                        </ol>
                        <ol v-else>
                          <li v-for="(step, i) in item.dispenseSteps" :key="i">
                            {{ step.volume | rounded }} gal / {{ step.weight | rounded }} lbs of
                            {{ step.chemical.name }}
                          </li>
                        </ol>
                      </v-col>
                    </v-row>
                  </v-container>
                </td>
              </template>
            </DataTable>
          </v-col>
        </v-row>
        <v-row v-if="overApiLimitWarning">
          <v-col>
            <v-alert type="warning" dense color="orange" border="left">
              {{ overApiLimitWarning }}
            </v-alert>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment-timezone';
import _ from 'lodash';
import { mapGetters } from 'vuex';

import Toastable from '@/mixins/toastable';
import AsyncComputable from '@/mixins/asyncComputable';
import ResourceService from '@/services/resources/dispense_logs_service';
import PlantUsersService from '@/services/resources/plant_users_service';
import ApplicationsService from '@/services/resources/applications_service';
import ChemicalsService from '@/services/resources/chemicals_service';

import downloader from '@/services/downloader';
import { dateFormat } from '@/utils/dates';

const API_LIMIT = 1000;
const API_LIMIT_WARNING_MESSAGE = 'Warning: This query has more results, reduce your time range.';

export default {
  mixins: [Toastable, AsyncComputable],
  components: {
    Loading: () => import('@/components/core/Loading.vue'),
    DataTable: () => import('@/components/core/DataTable.vue'),
    DateRangePicker: () => import('@/components/core/DateRangePicker.vue'),
  },
  data() {
    const dateMin =
      this.$route.query.dateMin ||
      moment()
        .subtract(1, 'days')
        .format(dateFormat);
    const dateMax = this.$route.query.dateMax || moment().format(dateFormat);

    return {
      sortBy: ['startTime'],
      dateMin,
      dateMax,
      timeMin: null,
      timeMax: null,
      filterPlantUsers: null,
      filterApps: null,
      filterChemicals: null,
      filterLocations: null,
      metricVolumeConversion: 3.78541,
      metricWeightConversion: 0.45359237,
      downloadOptions: [
        { title: 'Excel', callback: this.downloadExcel },
        { title: 'CSV', callback: this.downloadCSV },
      ],
      overApiLimitWarning: null,
      apiLimit: API_LIMIT,
    };
  },
  asyncComputed: {
    items: {
      get() {
        const filter = {
          site: this.siteId,
          timeMin: this.start.format(),
          timeMax: this.end.format(),
        };

        if (this.filterPlantUsers) {
          filter.plantUser = this.filterPlantUsers;
        }

        if (this.filterApps) {
          filter.app = this.filterApps;
        }

        if (this.filterChemicals) {
          filter.chemical = this.filterChemicals;
        }

        if (this.filterLocations) {
          filter.location = this.filterLocations;
        }

        if (this.$route.query.limit) {
          this.apiLimit = this.$route.query.limit;
        }

        const options = {
          include: 'plant-user,app,dispense-steps.chemical',
          filter,
          page: {
            limit: this.apiLimit,
          },
        };
        return ResourceService.index(options).then((items) => {
          this.overApiLimitWarning = null;
          if (items.data.length >= this.apiLimit) {
            this.overApiLimitWarning = API_LIMIT_WARNING_MESSAGE;
            this.infoToast(API_LIMIT_WARNING_MESSAGE);
          }
          return items.data;
        });
      },
      default: [],
      shouldUpdate() {
        return this.asyncComputedShouldUpdate();
      },
    },
    plantUsers: {
      get() {
        const options = {
          filter: {
            site: this.siteId,
          },
        };
        return PlantUsersService.index(options).then((items) => {
          return items.data;
        });
      },
      default: [],
      shouldUpdate() {
        return this.asyncComputedShouldUpdate();
      },
    },
    apps: {
      get() {
        const options = {
          filter: {
            site: this.siteId,
          },
        };
        return ApplicationsService.index(options).then((items) => {
          return items.data;
        });
      },
      default: [],
      shouldUpdate() {
        return this.asyncComputedShouldUpdate();
      },
    },
    chemicals: {
      get() {
        const options = {
          filter: {
            site: this.siteId,
          },
        };
        return ChemicalsService.index(options).then((items) => {
          return items.data;
        });
      },
      default: [],
      shouldUpdate() {
        return this.asyncComputedShouldUpdate();
      },
    },
    locations: {
      get() {
        return _.uniq(
          this.apps.map((app) => {
            return app.location;
          }),
        );
      },
      default: [],
      shouldUpdate() {
        return this.asyncComputedShouldUpdate();
      },
    },
  },
  computed: {
    ...mapGetters('app', ['sites', 'activeSite', 'activeUser']),
    start() {
      const start = moment.tz(this.dateMin, this.timeZone).startOf('day');
      if (this.timeMin) {
        const [hour, minute] = this.timeMin.split(':');
        start.set({ hour, minute });
      }
      return start;
    },
    end() {
      const end = moment.tz(this.dateMax, this.timeZone).endOf('day');
      if (this.timeMax) {
        const [hour, minute] = this.timeMax.split(':');
        end.set({ hour, minute });
      }
      return end;
    },
    activeSiteObject() {
      return _.find(this.sites, { id: this.activeSite });
    },
    timeZone() {
      return _.get(this.activeSiteObject, 'timeZone');
    },
    siteId() {
      return _.get(this.activeSiteObject, 'id');
    },
    isMetric() {
      return _.get(this.activeSiteObject, 'isMetric');
    },
    headers() {
      let volumeLabel = 'G';
      if (this.isMetric) {
        volumeLabel = 'L';
      }
      let weightLabel = 'lb';
      if (this.isMetric) {
        weightLabel = 'kg';
      }
      return [
        { text: 'Time', value: 'startTime' },
        { text: 'User', value: 'plantUser.name' },
        { text: 'Location', value: 'app.location' },
        { text: 'App Code', value: 'app.code' },
        { text: 'App Name', value: 'app.name' },
        { text: 'Error', value: 'errorMessage' },
        { text: `Target Volume (${volumeLabel})`, value: 'app.targetVolume', align: 'end' },
        { text: `Actual Volume (${volumeLabel})`, value: 'actualVolume', align: 'end' },
        { text: `Actual Weight (${weightLabel})`, value: 'actualWeight', align: 'end' },
      ];
    },
    reportItems() {
      return this.items.map((item) => {
        const formatted = {};
        this.headers.forEach((header) => {
          const value = _.get(item, header.value);
          if (_.isNil(value)) {
            formatted[header.text] = '';
          } else if (header.value === 'startTime') {
            formatted[header.text] = this.$options.filters.longDateFormat(value, this.timeZone);
          } else {
            formatted[header.text] = value;
          }
        });
        return formatted;
      });
    },
  },
  methods: {
    downloadExcel() {
      const site = this.activeSiteObject;
      const headerTable = [
        [''],
        ['Account Name:', site.name],
        ['Address:', site.address],
        ['', `${site.state} ${site.zipcode} ${site.country}`],
        [''],
        ['Account Group:', site.account],
        ['Generated By:', `${this.activeUser.firstName} ${this.activeUser.lastName}`],
        ['Generated On:', moment().toLocaleString()],
        [''],
        ['Start:', this.start.toLocaleString()],
        ['End:', this.end.toLocaleString()],
        [''],
        ['Dispense Logs:'],
      ];
      downloader.downloadDispenseLogsExcel(
        this.reportItems,
        this.headers,
        headerTable,
        'DispsenseLogReport',
      );
    },
    downloadCSV() {
      downloader.downloadDispenseLogsCSV(this.reportItems, this.headers, 'DispsenseLogReport');
    },
  },
};
</script>
